import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {NodeOutputData, NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import productFeedApi from 'common/src/modules/productFeed/productFeed.api';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';

const setStrategyToolSchema = z.object({
  strategyId: z.number().describe('The product selection strategy ID to set'),
});

export default function setStrategyTool() {
  async function execute({
    input: {strategyId},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof setStrategyToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    if (!validatedScratchpad.card.availableStrategies?.some((s) => s.strategyId === strategyId)) {
      return toolResult(
        `Strategy with ID ${strategyId} is not available for this cardץ Available strategies: ${validatedScratchpad.card.availableStrategies?.map((s) => `${s.strategyId}: ${s.name}`).join(', ')}`,
      );
    }

    //We need to remove related items
    validatedScratchpad.card.topItemLogicId = 0;
    validatedScratchpad.card.categoryTopItemsLogicId = 0;
    validatedScratchpad.card.retargetingProductSetId = 0;
    validatedScratchpad.card.productIds = [];
    validatedScratchpad.card.feedId = 0;

    const availableFeeds = await productFeedApi.getStrategyOptionalFeeds(strategyId);
    validatedScratchpad.card.availableFeeds = availableFeeds;
    validatedScratchpad.card.productsSelectionStrategy = strategyId;

    // Rebuild outputs with correct ordering: Success, Partial Success (if custom matching), Fallback
    const fallbackOutput = validatedScratchpad.card.outputs.find(
      (o) => o.type === OutputType.Fallback,
    );
    const successOutput = validatedScratchpad.card.outputs.find(
      (o) => o.type === OutputType.Regular,
    );

    const newOutputs: NodeOutputData[] = [];

    // Add Success output (always present)
    if (successOutput) {
      newOutputs.push(successOutput);
    } else {
      newOutputs.push({
        id: 1,
        name: 'Success',
        type: OutputType.Regular,
        nodeId: NO_NODE_ID,
      });
    }

    // Add Partial Success output only for Product Matching strategy
    if (strategyId === ProductSelectionStrategyType.ProductMatching) {
      newOutputs.push({
        id: 2,
        name: 'Partial Success',
        type: OutputType.PartialSuccess,
        nodeId: NO_NODE_ID,
      });
    }

    // Add Fallback output (always present)
    if (fallbackOutput) {
      newOutputs.push({...fallbackOutput, id: newOutputs.length + 1});
    } else {
      newOutputs.push({
        id: newOutputs.length + 1,
        name: 'Fallback',
        type: OutputType.Fallback,
        nodeId: NO_NODE_ID,
      });
    }

    validatedScratchpad.card.outputs = newOutputs;

    return toolResult(`Strategy set to: ${strategyId}`);
  }

  return tool({
    name: V2CardAgentToolName.SetStrategy,
    displayName: 'Set strategy',
    description: 'Set the product strategy for the card',
    execute,
    inputSchema: setStrategyToolSchema,
  });
}
